import { defaultInstance, directApiInstance } from './httpClient';
import type { User, Estate, UserEstateStatus } from './generated';

// UserEstate interface based on the provided JSON response
export interface UserEstate {
  user_id: number;
  estate_id: number;
  status_id: number;
  user_type_id: number;
  expiration_date: string | null;
  created_at: string;
  vacay_id: number | null;
  estate?: Estate;
  estate_group?: {
    id: number;
    name: string;
    tenant_id: string;
    home_url: string | null;
    logo_url: string | null;
  };
  user_type?: {
    id: number;
    name: string;
    is_register: boolean;
  };
  user_estate_status?: UserEstateStatus;
  user?: User;
}

// Response type for the user estate endpoint
export interface GetUserEstate200 {
  data: UserEstate[];
  total_pages: number;
  page: number;
  total_elements: number;
}

// Params type for the user estate endpoint
export interface GetUserEstateParams {
  page?: number;
  limit?: number;
  expand?: string;
  order_by?: string;
  order_direction?: 'ASC' | 'DESC';
  user_id?: number;
  estate_id?: number;
  status_id?: number;
  user_type_id?: number;
  user_search?: string;
  estate_search?: string;
}

/**
 * Returns a list of user_estate records with pagination and expanded relations
 * @summary Get all user_estate records with expanded data
 */
export const getUserEstate = (
  params?: GetUserEstateParams,
  signal?: AbortSignal
) => {
  return defaultInstance<GetUserEstate200>({
    url: '/user_estate',
    method: 'GET',
    params,
    signal
  });
};

export interface CreateUserEstatePayload {
  user_id: number;
  estate_id: number;
  user_type_id: number;
  status_id?: number;     // defaults backend-side; 1 = pending, 2 = approved
  expiration_date?: string | null;
  vacay_id?: number | null;
}

/**
 * Create a new user_estate row tying a user to an estate with a given role.
 * Mirrors the Flutter `requestNewEstate` helper in the cityplace app.
 */
export const createUserEstate = (
  data: CreateUserEstatePayload,
  signal?: AbortSignal,
) => {
  return directApiInstance({
    url: '/user_estate',
    method: 'POST',
    data,
    signal,
  });
}; 
export interface UpdateUserEstateRolePayload {
  user_id: number;
  estate_id: number;
  status_id: number;
  user_type_id: number;
}

/**
 * Change the role (`user_type_id`) of an existing user_estate row — SAM-974. Same PUT as the
 * status and the expiration date; the backend answers 403 unless the caller is in group `admin`.
 */
export const updateUserEstateRole = (data: UpdateUserEstateRolePayload, signal?: AbortSignal) =>
  directApiInstance({ url: '/user_estate', method: 'PUT', data, signal });

/** SAM-973 — the role control is read-only until the SAM-974 endpoint is deployed (Doppler VITE_USER_ESTATE_ROLE_EDIT). */
export const roleEditEnabled = (): boolean => import.meta.env.VITE_USER_ESTATE_ROLE_EDIT === 'true';

/** `null` clears the date ("No expiration"). */
export const updateUserEstateExpiration = (
  data: { user_id: number; estate_id: number; status_id: number; expiration_date: string | null },
  signal?: AbortSignal,
) => directApiInstance({ url: '/user_estate', method: 'PUT', data, signal });
